/**
 * @file   src\mobile\components\Onboarding\ProfileImageCropper.js
 * @brief  This file is responsible for handling profile image crop.
 * @date   June, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import React, { useEffect, useState } from "react";
import Cropper from "react-easy-crop";
import Modal from "react-bootstrap/Modal";
import { Row, Col, Button } from "react-bootstrap";
import { getPreSignedUrl } from "../../../services/fileUploadService";
import { toast } from "react-toastify";
import Loader from "../../../components/Common/Loader";

const ProfileImageCropper = (props) => {
  const { image, userId } = props;
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const isSquare = !!props?.isSquare;
  const s3ProfileImageFolder = `${isSquare ? "/logoImage/Images/" : "/profileImage/Images/"}`;
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    setZoom(1);
    setCrop({ x: 0, y: 0 });
    setCroppedAreaPixels(null);
  }, [props?.show]);

  const onCropComplete = (croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const handleSave = async () => {
    setIsUploading(true);

    let finalImage = image;

    if (!isSquare && croppedAreaPixels) {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      const imageRef = new Image();
      imageRef.src = image;

      await new Promise((resolve) => (imageRef.onload = resolve));

      canvas.width = croppedAreaPixels.width;
      canvas.height = croppedAreaPixels.height;

      ctx.drawImage(
        imageRef,
        croppedAreaPixels.x,
        croppedAreaPixels.y,
        croppedAreaPixels.width,
        croppedAreaPixels.height,
        0,
        0,
        croppedAreaPixels.width,
        croppedAreaPixels.height,
      );

      finalImage = canvas.toDataURL("image/jpeg");
    }

    const base64Data = finalImage.split(",")[1];
    const blob = base64ToBlob(base64Data, "image/jpeg");

    const reqPayload = {
      key: s3ProfileImageFolder + userId + ".jpg",
    };

    try {
      const response = await getPreSignedUrl(reqPayload);

      if (response.errorCode === 0) {
        const headersValue = new Headers({
          "Content-Type": "image/jpeg",
        });

        const uploadResponse = await fetch(response.result, {
          method: "PUT",
          headers: headersValue,
          body: blob,
        });

        if (uploadResponse.status === 200) {
          const fileFullUrl = uploadResponse.url;
          const fileS3Url = fileFullUrl ? fileFullUrl.split("?")[0] : "";

          setIsUploading(false);
          props.updateImageAfterCrop(fileS3Url);
          toast.success(
            `${isSquare ? "Logo" : "Profile"} image changed successfully`,
          );
        } else {
          throw new Error("Image upload failed");
        }
      } else {
        throw new Error(response.errorMessage);
      }
    } catch (error) {
      setIsUploading(false);
      toast.error(error.message);
    }
  };

  const base64ToBlob = (base64, type = "application/octet-stream") => {
    const binary = atob(base64);
    const array = [];
    for (let i = 0; i < binary.length; i++) {
      array.push(binary.charCodeAt(i));
    }
    return new Blob([new Uint8Array(array)], { type });
  };

  return (
    <>
      {isUploading && <Loader />}

      <Modal
        show
        {...props}
        size="md"
        aria-labelledby="contained-modal-title-vcenter"
        centered
        className="modal-settings"
        backdrop="static"
      >
        <Modal.Body className="pt-32 pb-32">
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={props.onHide}
          ></button>

          <Row>
            <Col xl={12}>
              <div className="textbox-wrap mt-3 mb-3 mb-sm-3 position-relative">
                {image && (
                  <>
                    {isSquare ? (
                      <img
                        src={image}
                        alt="Preview"
                        style={{
                          width: "100%",
                          maxHeight: "400px",
                          objectFit: "contain",
                        }}
                      />
                    ) : (
                      <>
                        <div
                          style={{
                            position: "relative",
                            width: "100%",
                            height: 400,
                          }}
                        >
                          <Cropper
                            image={image}
                            crop={crop}
                            zoom={zoom}
                            aspect={1}
                            cropShape="round"
                            onCropChange={setCrop}
                            onZoomChange={setZoom}
                            onCropComplete={onCropComplete}
                          />
                        </div>
                        <div className="modalImageSlider">
                          <input
                            type="range"
                            className="form-range"
                            id="customRange1"
                            value={zoom}
                            min={1}
                            max={3}
                            step={0.01}
                            onChange={(e) => setZoom(e.target.value)}
                            style={{ "--track-fg-width": "32%" }}
                          />
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            </Col>
          </Row>

          <div className="modal-btn-wrap mt-2">
            <Button
              variant=""
              className="btn btn-tertiary btn-margin-fix me-3"
              onClick={props.onHide}
            >
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave}>
              Save
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default ProfileImageCropper;
